/*
 * Open pull requests: what is in flight, whether review and CI agree, and
 * whether it still merges cleanly. Drafts are dimmed rather than hidden, since
 * a stalled draft is itself repository state worth seeing.
 */
import { CaretRight, CheckCircle, Merge, Warning, XCircle } from './Icons'
import { Avatar, Checks } from './Bits'
import { rel } from '../lib/format'
import type { PullRequest } from '../types'

function Review({ pr }: { pr: PullRequest }) {
  if (pr.changesRequested > 0 || pr.reviewDecision === 'CHANGES_REQUESTED') {
    return <span className="review changes"><XCircle size={13} /> changes requested</span>
  }
  if (pr.reviewDecision === 'APPROVED') {
    return <span className="review approved"><CheckCircle size={13} /> approved{pr.approvals > 1 ? ` ×${pr.approvals}` : ''}</span>
  }
  return <span className="review waiting"><Merge size={13} /> review pending</span>
}

export function PullRequests({ prs, total }: { prs: PullRequest[]; total: number }) {
  return (
    <section className="section">
      <div className="section-label">
        Open pull requests <span className="count">· {prs.length}{prs.length !== total ? ` of ${total}` : ''}</span>
      </div>
      {prs.length === 0 ? (
        <div className="empty">Nothing open. Every branch here is either merged or still local.</div>
      ) : (
        prs.map(pr => (
          <div className="pr" key={pr.number} style={pr.draft ? { opacity: 0.66 } : undefined}>
            <div className="pr-title">
              <span className="num">#{pr.number}</span>
              <a className="t" href={pr.url} target="_blank" rel="noreferrer noopener" title={pr.title}>{pr.title}</a>
              {pr.draft ? <span className="tag tag-neutral">draft</span> : null}
            </div>
            <div className="pr-refs" title={`${pr.head} → ${pr.base}`}>
              <span className="ref">{pr.head}</span>
              <span className="arrow"><CaretRight size={12} /></span>
              <span className="ref">{pr.base}</span>
            </div>
            <div className="pr-state">
              <Review pr={pr} />
              <Checks state={pr.checks} label />
              {pr.mergeable === 'CONFLICTING' ? (
                <span className="conflict" title="This branch has conflicts with its base"><Warning size={13} /> conflicts</span>
              ) : null}
            </div>
            <div className="pr-meta">
              <Avatar person={pr.author} />
              <span className="alias" style={{ fontSize: 12.5, color: 'var(--color-neutral-300)' }}>{pr.author.alias}</span>
              <span className="rel">{rel(pr.updatedAt)}</span>
            </div>
          </div>
        ))
      )}
    </section>
  )
}
